import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Routes, RouterModule } from '@angular/router';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { InnerLayoutComponent } from './layouts/inner/inner.component';

import { NavigationComponent } from './shared/header-navigation/navigation.component';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { BreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';

import { AppComponent } from './app.component';
import { SpinnerComponent } from './shared/spinner.component';

import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { NgxLoadingModule, ngxLoadingAnimationTypes } from 'ngx-loading';
import { AppRoutingModule, AppRoutes } from './app-routing.module';

import { ResponsiveService } from './shared/responsive.service';
import { GenericService, YearFileResolve } from './generic.service';
import { ToastrModule } from 'ngx-toastr';
import { DeviceDetectorModule } from 'ngx-device-detector';
import { OuterService } from './shared/outer.service';
import { AuthGuardService } from './shared/auth-guard.service';
import { AppConfig } from './shared/app.config';
import { TokenInterceptor } from './shared/auth.token';
import { SafePipe } from './shared/safeurl.pipe';
import { SharedModule } from './shared/shared.module';
import { MaterialModule } from './shared/material.module';
import { OuterLayoutComponent } from './layouts/outer/outer.component';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { PreviousYearDialogComponent } from './views-inner/shared/dialogs/previous-year-dialog/previous-year-dialog.component';
import { InfoDialogComponent } from './views-inner/shared/dialogs/info-dialog/info-dialog.component';
import { ViewDialogComponent } from './views-inner/shared/dialogs/view-dialog/view-dialog.component';
import { UploadDialogComponent } from './views-inner/shared/dialogs/upload-dialog/upload-dialog.component';
import { DeleteDialogComponent } from './views-inner/shared/dialogs/delete-dialog/delete-dialog.component';
import { OccupationDialogComponent } from './views-inner/shared/dialogs/occupation-dialog/occupation-dialog.component';
import { CalcDialogComponent } from './views-inner/shared/dialogs/calc-dialog/calc-dialog.component';
import { MaterialFileInputModule } from 'ngx-material-file-input';
import { MsgDialogComponent } from './views-inner/shared/dialogs/msg-dialog/msg-dialog.component';
import { PipeModule } from './shared/pipe.module';
import { OutUploadDialogComponent } from './views-outer/shared/out-upload-dialog/out-upload-dialog.component';
import { AttachmentUploadDialogComponent } from './views-inner/shared/dialogs/attachment-upload-dialog/attachment-upload-dialog.component';
import { ClickOutsideModule } from 'ng-click-outside';
import { LanderLayoutComponent } from './layouts/lander/lander.component';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer'; // <-- Import PdfJsViewerModule module
import { OutThanksComponent } from './views-outer/shared/out-thanks/out-thanks.component';
import { StripHtmlModule } from './shared/strip-html/strip-html.module';
import { OutEstimatePdfDialogComponent } from './views-outer/shared/out-estimate-pdf-dialog/out-estimate-pdf-dialog.component';
import { OutPdfViewDialogComponent } from './views-outer/shared/out-pdf-view-dialog/out-pdf-view-dialog.component';
import { IdVerifyDialogComponent } from './views-outer/shared/id-verify-dialog/id-verify-dialog.component';
import { OuterSharedModule } from './views-outer/shared/outer-shared.module';




const maskConfigFunction: () => Partial<IConfig> = () => {
  return {
    validation: true
  };
};

export function initializeApp(appConfig: AppConfig) {
  return () => appConfig.load();
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
  wheelSpeed: 1,
  wheelPropagation: true,
  minScrollbarLength: 20,
};

@NgModule({
  declarations: [
    AppComponent,
    SpinnerComponent,
    InnerLayoutComponent,
    OuterLayoutComponent,
    NavigationComponent,
    BreadcrumbComponent,
    SidebarComponent,
    PreviousYearDialogComponent,
    InfoDialogComponent,
    ViewDialogComponent,
    UploadDialogComponent,
    DeleteDialogComponent,
    OccupationDialogComponent,
    CalcDialogComponent,
    MsgDialogComponent,
    OutUploadDialogComponent,
    AttachmentUploadDialogComponent,
    LanderLayoutComponent,
    OutThanksComponent,
    OutEstimatePdfDialogComponent,
    OutPdfViewDialogComponent,
    IdVerifyDialogComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    NgbModule,
    HttpClientModule,
    RouterModule.forRoot(AppRoutes, { useHash: false }),
    PerfectScrollbarModule,
    DeviceDetectorModule.forRoot(),
    MaterialFileInputModule,
    NgxLoadingModule.forRoot({
      animationType: ngxLoadingAnimationTypes.circle,
      backdropBackgroundColour: 'rgba(0,0,0,0.3)',
      backdropBorderRadius: '5px',
      primaryColour: '#43A047',
      secondaryColour: '#444444',
      tertiaryColour: '#444444',
    }),
    ToastrModule.forRoot({
      preventDuplicates: true,
      enableHtml: true,
      progressBar: true,
      timeOut: 2000,
      maxOpened: 1,
      autoDismiss: true
    }),
    PipeModule.forRoot(),
    ClickOutsideModule,
    PdfJsViewerModule,
    NgxMaskModule.forRoot(maskConfigFunction),
    StripHtmlModule,
    OuterSharedModule
  ],
  providers: [
    AppConfig,
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [AppConfig], multi: true },
    // SignalrService,
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: (signalrService: SignalrService) => () => signalrService.initiateSignalrConnection(),
    //   deps: [SignalrService],
    //   multi: true,
    // },
    { provide: PERFECT_SCROLLBAR_CONFIG, useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    ResponsiveService,
    GenericService, OuterService, AuthGuardService
  ],
  bootstrap: [AppComponent],
  entryComponents: [PreviousYearDialogComponent, InfoDialogComponent, ViewDialogComponent, UploadDialogComponent, DeleteDialogComponent, OccupationDialogComponent, CalcDialogComponent, MsgDialogComponent, OutUploadDialogComponent,
    AttachmentUploadDialogComponent, OutThanksComponent, OutEstimatePdfDialogComponent, OutPdfViewDialogComponent, IdVerifyDialogComponent]
})
export class AppModule { }
